/**
 * Fetches Strapi CMS page content via ASA API (Redis → DB → Strapi).
 * Generic shape: content-type-specific fields are in data; UI is not coupled to TestPage.
 */

import { apiFetch } from '@/lib/apiFetch';

/** Strapi media format (thumbnail, small, medium, large). */
export interface StrapiImageFormat {
    url: string;
    width?: number;
    height?: number;
}

/** Strapi Image relation (articles and other types). */
export interface StrapiImage {
    id: number;
    url: string;
    name?: string;
    alternativeText?: string | null;
    formats?: {
        thumbnail?: StrapiImageFormat;
        small?: StrapiImageFormat;
        medium?: StrapiImageFormat;
        large?: StrapiImageFormat;
    };
}

export interface StrapiPageModel {
    slug: string;
    contentType: string;
    updatedAt: string;
    strapiId?: number;
    /** Content-type-specific attributes (e.g. title, description, cover, blocks, body). */
    data: Record<string, unknown>;
}

/**
 * Articles list item. `data` is a SUMMARY only — title/label, description, and cover (for the card thumbnail).
 * The full article body is not included here; the detail page fetches it via getArticleBySlug.
 */
/** One spotlight card as served by `GET /api/public/cms/spotlights`. */
export interface SpotlightListItem {
    slug: string;
    updatedAt: string;
    /** Full spotlight payload, including its `visibility` rules. */
    data: Record<string, unknown>;
}

export interface ArticlesListItem {
    slug: string;
    updatedAt: string;
    data: Record<string, unknown>;
}

function asStrapiImageShape(val: Record<string, unknown>): StrapiImage | undefined {
    if (typeof val.url !== 'string') return undefined;
    const id = typeof val.id === 'number' ? val.id : 0;
    return {
        id,
        url: val.url,
        name: typeof val.name === 'string' ? val.name : undefined,
        alternativeText: val.alternativeText === null || typeof val.alternativeText === 'string'
            ? val.alternativeText
            : undefined,
        formats: val.formats as StrapiImage['formats'],
    };
}

/** Unwrap Strapi media: flat populated object, or v4-style { data: { id, attributes } }. */
export function resolveArticleMediaField(val: unknown): StrapiImage | undefined {
    if (val == null) return undefined;
    if (Array.isArray(val)) {
        for (const item of val) {
            const resolved = resolveArticleMediaField(item);
            if (resolved) return resolved;
        }
        return undefined;
    }
    if (typeof val !== 'object') return undefined;
    const o = val as Record<string, unknown>;
    const direct = asStrapiImageShape(o);
    if (direct) return direct;

    // Entry shape: { id, attributes: { url, ... } } (common in populated arrays).
    if (o.attributes != null && typeof o.attributes === 'object') {
        const attrs = o.attributes as Record<string, unknown>;
        const id = typeof o.id === 'number' ? o.id : typeof attrs.id === 'number' ? attrs.id : 0;
        const fromAttrs = asStrapiImageShape({ ...attrs, id });
        if (fromAttrs) return fromAttrs;
    }

    const wrapped = o.data;
    if (wrapped == null || typeof wrapped !== 'object') return undefined;
    const d = wrapped as Record<string, unknown>;
    if (d.attributes != null && typeof d.attributes === 'object') {
        const attrs = d.attributes as Record<string, unknown>;
        const id = typeof d.id === 'number' ? d.id : typeof attrs.id === 'number' ? attrs.id : 0;
        return asStrapiImageShape({ ...attrs, id });
    }
    return asStrapiImageShape(d);
}

/**
 * Featured image for resource articles. Strapi schema uses `cover`; older payloads may use `Image`.
 */
export function getArticleFeaturedImage(data: Record<string, unknown>): StrapiImage | undefined {
    return resolveArticleMediaField(data.Image) ?? resolveArticleMediaField(data.cover);
}

/** Thumbnail URL for list cards (prefers small derivations, then full). */
export function getArticleThumbnailUrl(data: Record<string, unknown>): string | undefined {
    const image = getArticleFeaturedImage(data);
    if (!image) return undefined;
    return image.formats?.thumbnail?.url ?? image.formats?.small?.url ?? image.url;
}

/** Full-size featured image URL for article detail. */
export function getArticleImageUrl(data: Record<string, unknown>): string | undefined {
    return getArticleFeaturedImage(data)?.url;
}

/**
 * Resolve a Strapi multiple-media field (flat array, or v4-style `{ data: [...] }`) into images.
 */
export function resolveArticleMediaList(val: unknown): StrapiImage[] {
    if (val == null) return [];
    if (Array.isArray(val)) {
        const out: StrapiImage[] = [];
        for (const item of val) {
            const resolved = resolveArticleMediaField(item);
            if (resolved) out.push(resolved);
        }
        return out;
    }
    if (typeof val === 'object') {
        const o = val as Record<string, unknown>;
        if (Array.isArray(o.data)) {
            return resolveArticleMediaList(o.data);
        }
        const single = resolveArticleMediaField(val);
        return single ? [single] : [];
    }
    return [];
}

/**
 * Fields every dynamic-zone block carries. `visibility` holds the raw CMS targeting rules
 * (ASA-1570); it is passed through unparsed so this module stays free of the evaluator, and
 * consumers filter with `isVisible` from `@/lib/visibility/evaluateVisibility`.
 */
interface ArticleContentBlockBase {
    key: string;
    visibility?: unknown;
}

/** Ordered article dynamic-zone blocks we render on resource detail and /cms-zones. */
export type CalloutVariant = 'success' | 'error' | 'warning' | 'info';

export type ArticleContentBlock
    = | (ArticleContentBlockBase & { kind: 'rich-text'; body: string })
        | (ArticleContentBlockBase & { kind: 'slider'; files: StrapiImage[] })
        | (ArticleContentBlockBase & { kind: 'media'; file: StrapiImage })
        | (ArticleContentBlockBase & { kind: 'quote'; title?: string; body?: string })
        | (ArticleContentBlockBase & { kind: 'button'; caption: string; url: string })
        | (ArticleContentBlockBase & { kind: 'callout'; variant: CalloutVariant; body: string });

/**
 * Entry-level targeting rules (an article, or a single type). Returns undefined when the
 * entry carries no `visibility` field, which the evaluator reads as "visible to everyone".
 */
export function getEntryVisibility(data: Record<string, unknown>): unknown {
    return data.visibility;
}

const KNOWN_DYNAMIC_ZONE_COMPONENTS = new Set([
    'shared.rich-text',
    'shared.slider',
    'shared.media',
    'shared.quote',
    'shared.button',
    'shared.callout',
]);

/**
 * Schemes a CMS author may link to from a Button block. The caption and URL are author-supplied
 * and the URL lands in an `href` we render verbatim, so `javascript:` and friends are rejected
 * here rather than in the component.
 */
const ALLOWED_BUTTON_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/**
 * A bare word like `faqs` is how CMS authors write an in-app link, so it is rooted to `/faqs`
 * rather than dropped. A value with a dot before the first slash (`example.com/x`) is ambiguous
 * — most likely a host someone forgot to put a scheme on — so it is rejected instead of being
 * silently turned into an internal path that goes nowhere.
 */
function rootBareAppPath(url: string): string | null {
    const firstSegment = url.split('/', 1)[0];
    return firstSegment.includes('.') ? null : `/${url}`;
}

/**
 * Strapi Shared Button: `caption` and `url`. Returns null (block dropped, warning logged) when
 * either field is missing, or the URL uses a scheme outside `ALLOWED_BUTTON_PROTOCOLS`.
 */
function parseButtonBlock(b: Record<string, unknown>): { caption: string; url: string } | null {
    const caption = typeof b.caption === 'string' ? b.caption.trim() : '';
    const raw = typeof b.url === 'string' ? b.url.trim() : '';
    if (!caption || !raw) return null;
    // A rooted path stays in the SPA; `//host` is protocol-relative and therefore external.
    if (raw.startsWith('/') && !raw.startsWith('//')) {
        return { caption, url: raw };
    }
    if (!/^[a-z][a-z0-9+.-]*:/i.test(raw) && !raw.startsWith('//')) {
        const rooted = rootBareAppPath(raw);
        return rooted ? { caption, url: rooted } : null;
    }
    try {
        if (!ALLOWED_BUTTON_PROTOCOLS.has(new URL(raw).protocol)) return null;
    }
    catch {
        return null;
    }
    return { caption, url: raw };
}

function inlineBlocksToMarkdown(nodes: unknown): string {
    if (!Array.isArray(nodes)) return '';
    return nodes.map((node) => {
        if (node == null || typeof node !== 'object') return '';
        const item = node as Record<string, unknown>;
        if (item.type === 'link') {
            const label = inlineBlocksToMarkdown(item.children);
            const url = typeof item.url === 'string' ? item.url : '';
            return url ? `[${label}](${url})` : label;
        }
        if (Array.isArray(item.children) && typeof item.text !== 'string') {
            return inlineBlocksToMarkdown(item.children);
        }
        let text = typeof item.text === 'string' ? item.text : '';
        if (!text) return '';
        if (item.code === true) text = `\`${text}\``;
        if (item.bold === true) text = `**${text}**`;
        if (item.italic === true) text = `*${text}*`;
        if (item.strikethrough === true) text = `~~${text}~~`;
        return text;
    }).join('');
}

/** Strapi Blocks rich text, or a markdown string, as markdown the alert can render. */
export function strapiBlocksToMarkdown(value: unknown): string {
    if (typeof value === 'string') return value.trim();
    if (!Array.isArray(value)) return '';
    const parts: string[] = [];
    for (const block of value) {
        if (block == null || typeof block !== 'object') continue;
        const item = block as Record<string, unknown>;
        const type = typeof item.type === 'string' ? item.type : '';
        if (type === 'list' && Array.isArray(item.children)) {
            const ordered = item.format === 'ordered';
            item.children.forEach((child, index) => {
                if (child == null || typeof child !== 'object') return;
                const line = inlineBlocksToMarkdown((child as Record<string, unknown>).children).trim();
                if (!line) return;
                parts.push(`${ordered ? `${index + 1}.` : '-'} ${line}`);
            });
            continue;
        }
        const line = inlineBlocksToMarkdown(item.children).trim();
        if (!line) continue;
        if (type === 'heading') {
            const level = typeof item.level === 'number' ? Math.min(6, Math.max(1, item.level)) : 2;
            parts.push(`${'#'.repeat(level)} ${line}`);
            continue;
        }
        if (type === 'quote') {
            parts.push(`> ${line}`);
            continue;
        }
        parts.push(line);
    }
    return parts.join('\n\n');
}

/** Callout `type` selects a toast-aligned alert. Anything else is info. */
export function calloutVariant(type: unknown): CalloutVariant {
    const value = typeof type === 'string' ? type.trim().toLowerCase() : '';
    if (value === 'success') return 'success';
    if (value === 'error') return 'error';
    if (value === 'warning') return 'warning';
    if (value === 'info') return 'info';
    return 'info';
}

/** Strapi Shared Quote: `title` and `body` text fields only. */
function parseQuoteBlock(b: Record<string, unknown>): { title?: string; body?: string } | null {
    const title = typeof b.title === 'string' ? b.title.trim() : '';
    const body = typeof b.body === 'string' ? b.body.trim() : '';
    if (!title && !body) return null;
    return {
        ...(title ? { title } : {}),
        ...(body ? { body } : {}),
    };
}

/**
 * Parse `data.blocks` into renderable content blocks in CMS order.
 * Unknown `__component` values are skipped (with a dev warning) so they cannot crash the page.
 */
export function getArticleContentBlocks(data: Record<string, unknown>): ArticleContentBlock[] {
    const blocks = data.blocks;
    if (!Array.isArray(blocks)) return [];

    const out: ArticleContentBlock[] = [];
    blocks.forEach((block, index) => {
        if (block == null || typeof block !== 'object') return;
        const b = block as Record<string, unknown>;
        const component = typeof b.__component === 'string' ? b.__component : '';
        const idPart = typeof b.id === 'number' || typeof b.id === 'string' ? String(b.id) : String(index);
        const key = `${component}-${idPart}-${index}`;
        // Raw rules ride along on every block; filtering happens at the consumer.
        const vis = b.visibility !== undefined ? { visibility: b.visibility } : {};

        if (component && !KNOWN_DYNAMIC_ZONE_COMPONENTS.has(component)) {
            console.warn(`Unsupported CMS component: ${component}`);
            return;
        }

        if (component === 'shared.rich-text') {
            if (typeof b.body === 'string' && b.body.trim()) {
                out.push({ kind: 'rich-text', key, ...vis, body: b.body.trim() });
            }
            return;
        }

        if (component === 'shared.slider') {
            const files = resolveArticleMediaList(b.files);
            if (files.length > 0) {
                out.push({ kind: 'slider', key, ...vis, files });
            }
            return;
        }

        if (component === 'shared.media') {
            const file = resolveArticleMediaField(b.file);
            if (file) {
                out.push({ kind: 'media', key, file, ...vis });
            }
            return;
        }

        if (component === 'shared.quote') {
            const quote = parseQuoteBlock(b);
            if (quote) {
                out.push({ kind: 'quote', key, ...vis, ...quote });
            }
            return;
        }

        if (component === 'shared.button') {
            const button = parseButtonBlock(b);
            if (button) {
                out.push({ kind: 'button', key, ...vis, ...button });
            }
            else {
                console.warn(`Unusable CMS button block: ${key}`);
            }
            return;
        }

        if (component === 'shared.callout') {
            const body = strapiBlocksToMarkdown(b.body);
            if (body) {
                out.push({ kind: 'callout', key, ...vis, variant: calloutVariant(b.type), body });
            }
        }
    });

    return out;
}

/**
 * Markdown from Strapi dynamic zone `blocks` (e.g. `shared.rich-text` with a `body` field).
 * Use when there is no top-level `body` / `content` string. Does not include Slider images.
 */
export function getArticleBlocksMarkdown(data: Record<string, unknown>): string | undefined {
    const parts = getArticleContentBlocks(data)
        .filter((b): b is Extract<ArticleContentBlock, { kind: 'rich-text' }> => b.kind === 'rich-text')
        .map(b => b.body);
    if (parts.length === 0) return undefined;
    return parts.join('\n\n');
}

export const strapiService = {
    getPageBySlug: async (slug: string): Promise<StrapiPageModel | null> => {
        const res = await apiFetch(`/api/public/cms/pages/${encodeURIComponent(slug)}`);
        if (res.status === 404) {
            return null;
        }
        if (!res.ok) {
            throw new Error(`Failed to load page: ${res.status} ${res.statusText}`);
        }
        return res.json() as Promise<StrapiPageModel>;
    },

    /**
     * Spotlight entries, served from our own store (Redis → DB) like every other CMS read.
     * Content arrives via the Strapi webhook and syncCmsJob; this never calls Strapi.
     */
    listSpotlights: async (): Promise<SpotlightListItem[]> => {
        const res = await apiFetch('/api/public/cms/spotlights');
        if (!res.ok) {
            throw new Error(`Failed to load spotlights: ${res.status} ${res.statusText}`);
        }
        const data = (await res.json()) as unknown;
        return Array.isArray(data) ? data as SpotlightListItem[] : [];
    },

    listArticles: async (): Promise<ArticlesListItem[]> => {
        const res = await apiFetch('/api/public/cms/articles');
        if (!res.ok) {
            throw new Error(`Failed to load articles list: ${res.status} ${res.statusText}`);
        }
        const data = (await res.json()) as ArticlesListItem[];
        return Array.isArray(data) ? data : [];
    },

    /**
     * Published changelog entries from our store (Redis → DB), the same path as the article list.
     * This does not call Strapi.
     */
    listChangelogs: async (): Promise<ArticlesListItem[]> => {
        const res = await apiFetch('/api/public/cms/changelogs');
        if (!res.ok) {
            throw new Error(`Failed to load changelog: ${res.status} ${res.statusText}`);
        }
        const data = (await res.json()) as unknown;
        return Array.isArray(data) ? data as ArticlesListItem[] : [];
    },

    /** One published changelog entry. This does not call Strapi. */
    getChangelogBySlug: async (slug: string): Promise<StrapiPageModel | null> => {
        const res = await apiFetch(`/api/public/cms/changelogs/${encodeURIComponent(slug)}`);
        if (res.status === 404) {
            return null;
        }
        if (!res.ok) {
            throw new Error(`Failed to load changelog: ${res.status} ${res.statusText}`);
        }
        return res.json() as Promise<StrapiPageModel>;
    },

    getArticleBySlug: async (slug: string): Promise<StrapiPageModel | null> => {
        const res = await apiFetch(`/api/public/cms/articles/${encodeURIComponent(slug)}`);
        if (res.status === 404) {
            return null;
        }
        if (!res.ok) {
            throw new Error(`Failed to load article: ${res.status} ${res.statusText}`);
        }
        return res.json() as Promise<StrapiPageModel>;
    },
};
